import { nanoid } from "nanoid";
import {
	type PresentOptionsOutput,
	presentOptionsOutputSchema,
} from "./schemas/present-options";
import type { AgentChatChoiceOption, AgentChatMessage } from "./types";

export type { PresentOptionsOutput };

export function createPresentOptionsOutput(
	prompt: string,
	options: AgentChatChoiceOption[],
): PresentOptionsOutput {
	return {
		choiceId: nanoid(),
		prompt,
		options,
		awaitingSelection: true,
	};
}

export function isPresentOptionsOutput(
	output: unknown,
): output is PresentOptionsOutput {
	return presentOptionsOutputSchema.safeParse(output).success;
}

export function parsePresentOptionsOutput(
	output: unknown,
): PresentOptionsOutput {
	return presentOptionsOutputSchema.parse(output);
}

export function formatChoiceSelectionMessage(label: string) {
	return `Selected: ${label}`;
}

export function findChoiceRequest(
	messages: AgentChatMessage[],
	choiceId: string,
) {
	for (let index = messages.length - 1; index >= 0; index -= 1) {
		const message = messages[index];
		if (message?.role !== "assistant") {
			continue;
		}

		const requestPart = message.parts.find(
			(part) => part.type === "choice-request" && part.choiceId === choiceId,
		);

		if (requestPart?.type === "choice-request") {
			return requestPart;
		}
	}

	return null;
}

export function validateChoiceSelection(
	messages: AgentChatMessage[],
	choiceId: string,
	optionId: string,
): { label: string } | null {
	const request = findChoiceRequest(messages, choiceId);
	if (!request) {
		return null;
	}

	const option = request.options.find((candidate) => candidate.id === optionId);
	if (!option) {
		return null;
	}

	return { label: option.label };
}

export function markChoiceResponded(
	messages: AgentChatMessage[],
	choiceId: string,
	optionId: string,
	label: string,
): AgentChatMessage[] {
	const nextMessages = [...messages];

	for (let index = nextMessages.length - 1; index >= 0; index -= 1) {
		const message = nextMessages[index];
		if (message?.role !== "assistant") {
			continue;
		}

		const hasRequest = message.parts.some(
			(part) => part.type === "choice-request" && part.choiceId === choiceId,
		);
		if (!hasRequest) {
			continue;
		}

		const alreadyResponded = message.parts.some(
			(part) => part.type === "choice-response" && part.choiceId === choiceId,
		);
		if (alreadyResponded) {
			return messages;
		}

		nextMessages[index] = {
			...message,
			parts: [
				...message.parts,
				{
					type: "choice-response",
					choiceId,
					optionId,
					label,
				},
			],
		};

		return nextMessages;
	}

	return messages;
}

export function getPendingChoice(messages: AgentChatMessage[]) {
	const lastMessage = messages[messages.length - 1];
	if (!lastMessage || lastMessage.role !== "assistant") {
		return null;
	}

	const respondedChoiceIds = new Set(
		lastMessage.parts
			.filter((part) => part.type === "choice-response")
			.map((part) => part.choiceId),
	);

	for (let index = lastMessage.parts.length - 1; index >= 0; index -= 1) {
		const part = lastMessage.parts[index];
		if (part.type !== "choice-request") {
			continue;
		}

		if (!respondedChoiceIds.has(part.choiceId)) {
			return part;
		}
	}

	return null;
}

export function collectChoiceResponses(messages: AgentChatMessage[]) {
	const responses: Array<{
		choiceId: string;
		optionId: string;
		label: string;
	}> = [];

	for (const message of messages) {
		if (message.role !== "assistant") {
			continue;
		}

		for (const part of message.parts) {
			if (part.type === "choice-response") {
				responses.push({
					choiceId: part.choiceId,
					optionId: part.optionId,
					label: part.label,
				});
			}
		}
	}

	return responses;
}

export const GENERATION_ALL_MODELS_OPTION_ID = "all-models";

export function assertGenerationAuthorized(
	messages: AgentChatMessage[],
	options: { model?: string; allModels: boolean },
): void {
	const responses = collectChoiceResponses(messages);

	if (options.allModels) {
		const authorized = responses.some(
			(response) => response.optionId === GENERATION_ALL_MODELS_OPTION_ID,
		);
		if (!authorized) {
			throw new Error(
				"User must select all-models via presentOptions before running all-model generation",
			);
		}
	}
}
